/**
 * Mystery hand results - Firestore I/O.
 *
 *   mysteryHandResults/{handId}/entries/{uid}
 *     { uid, handId, name, made, tricks, contract: {level, strain}, tries,
 *       madeAt (server time of the FIRST make, null until then), lastPlayedAt }
 *
 * Rules (firestore.rules): anyone can read (the board is public), a member
 * writes only their own entry, and once `made` is true the result is frozen -
 * only `tries` / `lastPlayedAt` may still change. First make counts; playing
 * again never moves you down.
 */
import { firebase } from "../../firebase/config";

const db = () => firebase.firestore();
const entries = (handId) => db().collection("mysteryHandResults").doc(handId).collection("entries");

// "Paul Dalley" -> "Paul D" (the weekly tournament's privacy rule).
export function shortName(name) {
  // Never show an email address on a public list (some members typed theirs into the name box).
  if (/@/.test(String(name || ""))) return "Member";
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Player";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`;
}

/**
 * The name this member has chosen for the boards (/this-month, "Your name on the
 * board"): "Anonymous", a name of their own, or - when they have chosen nothing -
 * first name and initial from the account. Read from their own membersData doc.
 */
async function boardNameFor(uid, fallbackName) {
  try {
    const snap = await db().collection("membersData").doc(uid).get();
    const d = snap.exists ? snap.data() : {};
    if (d.boardAnonymous === true) return "Anonymous";
    if (typeof d.boardName === "string" && d.boardName.trim() && !/@/.test(d.boardName)) return d.boardName.trim().slice(0, 40);
  } catch (_) {}
  return shortName(fallbackName).slice(0, 40);
}

/**
 * Names for a hand's list as the server shows them, keyed by the time of the make.
 * An entry's own `name` is frozen once the hand is made, so a member who later
 * goes Anonymous or renames would still show under the old one; the API applies
 * their current choice. Empty map on localhost (no API there) or on any failure,
 * and the list then falls back to the entries' own names.
 */
export async function fetchBoardNames(handId, count) {
  const names = new Map();
  try {
    const r = await fetch(`/api/mystery-leaderboard?hand=${encodeURIComponent(handId)}&n=${Number(count) || 0}`);
    if (!r.ok) return names;
    const d = await r.json();
    (d.made || []).forEach((m) => { if (m && m.madeAt) names.set(Number(m.madeAt), String(m.name || "")); });
  } catch (_) {}
  return names;
}

export function contractMade(record) {
  const c = record && record.contract;
  if (!c || record.passout) return false;
  return Number(record.declarerTricks) >= Number(c.level) + 6;
}

/**
 * Record one finished hand for this member. Returns the entry as it now stands
 * plus `firstMake` (true when this play put them on the board).
 */
export async function recordMysteryResult(handId, uid, name, record) {
  const ref = entries(handId).doc(uid);
  const boardName = await boardNameFor(uid, name);
  const made = contractMade(record);
  const tricks = Number(record.declarerTricks) || 0;
  const now = firebase.firestore.FieldValue.serverTimestamp();
  return db().runTransaction(async (tx) => {
    const snap = await tx.get(ref);
    const cur = snap.exists ? snap.data() : null;
    const tries = ((cur && cur.tries) || 0) + 1;
    if (cur && cur.made === true) {
      tx.update(ref, { tries, lastPlayedAt: now });
      return { ...cur, tries, firstMake: false };
    }
    const entry = {
      uid,
      handId,
      name: boardName,
      made,
      tricks,
      contract: { level: record.contract.level, strain: record.contract.strain },
      tries,
      madeAt: made ? now : null,
      lastPlayedAt: now,
    };
    tx.set(ref, entry);
    return { ...entry, firstMake: made };
  });
}

/**
 * Live leaderboard. `onNext({ made: [...], tried: n }, err)` - `made` sorted by
 * the time of the first make (earliest first), `tried` = everyone who has
 * finished the hand at least once, made or not.
 */
export function subscribeMysteryLeaderboard(handId, onNext) {
  return entries(handId).onSnapshot(
    (snap) => {
      const made = [];
      let tried = 0;
      snap.forEach((doc) => {
        const d = doc.data();
        tried += 1;
        if (d.made !== true) return;
        const t = d.madeAt && typeof d.madeAt.toMillis === "function" ? d.madeAt.toMillis() : Number.MAX_SAFE_INTEGER;
        made.push({
          uid: doc.id,
          name: String(d.name || "Player"),
          tricks: Number(d.tricks) || 0,
          contract: d.contract || null,
          tries: Number(d.tries) || 1,
          madeAtMs: t,
        });
      });
      made.sort((a, b) => a.madeAtMs - b.madeAtMs);
      onNext({ made, tried }, null);
    },
    (err) => onNext({ made: [], tried: 0 }, err)
  );
}
